import DOMPurify from 'dompurify';

import { RICH_TEXT_CLASS } from '@/lib/rich-text';
import { cn, isHtmlContent } from '@/lib/utils';

/**
 * The sanitizer allowlist for stored editor HTML.
 *
 * Exported so `isHtmlContent()`'s tag list can be asserted against it: a tag
 * this admits but the detector misses renders its own markup to the reader and
 * is then escaped into the stored value on the next open. The two lists are one
 * invariant, not two independent constants.
 */
export const SAFE_EDITOR_MARKUP = {
  ALLOWED_TAGS: [
    'p',
    'br',
    'strong',
    'b',
    'em',
    'i',
    'u',
    's',
    'strike',
    // `del` too: Tiptap's Strike mark parses it, `RichTextPdf` maps it, and a
    // paste from Word/Docs can leave one in the stored HTML. Dropping it here
    // would strike text through on the PDF copy and not on screen.
    'del',
    'ul',
    'ol',
    'li',
    'h1',
    'h2',
    'h3',
    'h4',
    'h5',
    'h6',
    'blockquote',
    'a',
    'code',
    'pre',
    'hr',
  ],
  // Formatting is supplied by the host's fixed RICH_TEXT_CLASS. Stored issue
  // text must not inject positioning classes, inline CSS, or colliding IDs.
  ALLOWED_ATTR: ['href', 'target', 'rel'],
  FORBID_ATTR: ['class', 'style', 'id'],
};

/**
 * The phrasing subset for a description that lives INSIDE a control (an add-on
 * card is a `<button>`): text marks only. Links are unwrapped to their text —
 * an `<a>` inside a button is invalid HTML and a click on it would both toggle
 * the card and navigate — and block structure is flattened to line breaks.
 */
const SAFE_PHRASING_MARKUP = {
  ALLOWED_TAGS: ['br', 'strong', 'b', 'em', 'i', 'u', 's', 'strike', 'del', 'span', 'code'],
  ALLOWED_ATTR: [] as string[],
  FORBID_ATTR: ['class', 'style', 'id'],
};

/** Block boundaries → line breaks and bullets, so a flattened list stays legible. */
function flattenBlocks(html: string): string {
  return html
    .replace(/<li[^>]*>/gi, '• ')
    .replace(/<\/(?:p|li|h[1-6]|div|blockquote|pre)>/gi, '<br>')
    .replace(/<hr[^>]*>/gi, '<br>')
    .replace(/(?:\s*<br\s*\/?>)+\s*$/i, '');
}

interface RichTextContentProps {
  content: string;
  className?: string;
  /**
   * Host element. `span` for phrasing contexts — a description inside a
   * `<button>` card, whose content model admits neither a `<div>` nor a link:
   * the span host renders the phrasing subset (marks only, links unwrapped,
   * blocks flattened to line breaks).
   */
  as?: 'div' | 'span';
}

/** Render newly-authored rich text while keeping legacy plain summaries readable. */
export function RichTextContent({ content, className, as: Host = 'div' }: RichTextContentProps) {
  if (!isHtmlContent(content)) {
    return <Host className={cn('whitespace-pre-wrap', className)}>{content}</Host>;
  }

  if (Host === 'span') {
    return (
      <span
        className={className}
        dangerouslySetInnerHTML={{
          __html: DOMPurify.sanitize(flattenBlocks(content), SAFE_PHRASING_MARKUP),
        }}
      />
    );
  }

  return (
    <div
      className={cn('max-w-none', RICH_TEXT_CLASS, className)}
      dangerouslySetInnerHTML={{ __html: DOMPurify.sanitize(content, SAFE_EDITOR_MARKUP) }}
    />
  );
}
